import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInstaller } from '../InstallerDownload';
import { produktSeite, SICHTBAR } from '../../data/produktSeiten';
import { Fx, Bild, AnsichtBox, useGleichHoch, titelBild, titelPfad } from './Kopf';
import Lichtszene from '../licht/Lichtszene';
import DemoPlayer from './DemoPlayer';
import AboZustimmung from '../kauf/AboZustimmung';
import './produktseite.css';

// Neue Produktseite nach dem Entwurf ~/Projects/davidmedia-header-test/produkt.html (04.10.2026):
// Kopf mit Kaufbox und 3D-Karte (Ansichten aus der Extension), „In Aktion“ (Video), „So
// funktioniert's“ als Zeitleiste unter dem Video, Installation, Einzeln oder alles, Fragen,
// weitere Werkzeuge. Inhalte: data/produktSeiten.js; Preise und Kauf kommen aus ProductDetail.

const euro = (en, v, stellen = 2) => {
  const z = v.toFixed(stellen);
  return en ? '€' + z : z.replace('.', ',') + ' €';
};
const ABO_JAHR = 159, ABO_MONAT = 15.99;
// „All-in-One“ nie am Bindestrich umbrechen (geschützte Bindestriche U+2011)
const ganz = t => t && t.replace(/All-in-One/g, 'All\u2011in\u2011One');

export default function Produktseite({ id, produkt, en, alleProdukte, onWarenkorb, onDownload, geladen }) {
  const S = useMemo(() => produktSeite(id, produkt, en), [id, produkt, en]);
  // Standbild im „In Aktion“-Block, solange es kein Video gibt
  const standbild = useMemo(() => {
    const finde = muster => S.ansichten.find(a => a.src && muster.test(a.src));
    return finde(/\/produkt\/einstellungen\//) || finde(/\/produkt\/vorlagen\/|\/startseite\/panel/) || { src: S.titel };
  }, [S]);
  const p = en ? '/en' : '';
  const wurzel = useRef(null);
  const info = useInstaller();
  const [hatVideo, setHatVideo] = useState(false);
  const T = (de, enText) => (en ? enText : de);
  // Zustimmung vor der Abo-Kasse: null = zu, sonst die Laufzeit, mit der der Dialog aufgeht ('monat' | 'jahr').
  // Alle „7 Tage kostenlos testen“-Knöpfe der Seite öffnen ihn, statt erst zur All-in-One-Seite zu führen.
  const [abo, setAbo] = useState(null);

  useEffect(() => {
    const el = wurzel.current;
    // Einblenden beim Scrollen
    const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }), { threshold: .12 });
    el.querySelectorAll('.reveal, .reveal-text').forEach(x => io.observe(x));
    // Gestaffeltes Einblenden nur beim ersten Mal: danach reagiert Hover ohne Verzögerung
    const nachEinblenden = e => {
      if (e.propertyName === 'opacity' && e.target.classList && e.target.classList.contains('reveal')) e.target.style.transitionDelay = '0s';
    };
    el.addEventListener('transitionend', nachEinblenden);
    // Light Sweep versetzt starten
    el.querySelectorAll('.btn.primaer').forEach(b => b.style.setProperty('--sv', (1 + Math.random() * 4).toFixed(2) + 's'));
    // Glas-Ränder bekommen die Cursor-Position
    const bewegen = e => {
      const fx = e.target.closest && e.target.closest('.fx');
      if (fx && el.contains(fx)) {
        const r = fx.getBoundingClientRect();
        fx.style.setProperty('--mx', (e.clientX - r.left).toFixed(0) + 'px');
        fx.style.setProperty('--my', (e.clientY - r.top).toFixed(0) + 'px');
      }
    };
    window.addEventListener('pointermove', bewegen, { passive: true });
    return () => { io.disconnect(); el.removeEventListener('transitionend', nachEinblenden); window.removeEventListener('pointermove', bewegen); };
  }, []);

  // Kopf: Box und Textspalte gleich hoch, ohne das Bild zu beschneiden
  const heroInnen = useGleichHoch([id, en]);

  const zuAbschnitt = (e, sel) => {
    const ziel = wurzel.current && wurzel.current.querySelector(sel);
    if (!ziel) return;
    e.preventDefault();
    window.scrollTo({ top: ziel.getBoundingClientRect().top + window.scrollY - 40, behavior: 'smooth' });
  };

  const art = S.art;
  const mitInstaller = art === 'werkzeug' || art === 'abo' || art === 'gratis-panel';
  const gratis = art === 'gratis' || art === 'gratis-panel';
  const [erstes, ...rest] = S.name.split(' ');
  // im Fließtext mit Artikel: „das Flow Panel“
  const nameImSatz = art === 'gratis-panel' ? T(`das ${S.name}`, `the ${S.name}`) : S.name;
  const preis = typeof produkt.priceValue === 'number' ? produkt.priceValue : 0;
  const monatAusJahr = euro(en, ABO_JAHR / 12);

  const KATEGORIE_GRATIS = { 'After Effects Presets': T('After-Effects-Presets', 'After Effects presets'), 'After Effects Project File': T('After-Effects-Projekt', 'After Effects project'), Photoshop: 'Photoshop' };
  const kicker = {
    werkzeug: T('Extension für After Effects', 'Extension for After Effects'),
    abo: T('Nur im All-in-One', 'Only in All-in-One'),
    'gratis-panel': T('Kostenlose Extension', 'Free extension'),
    gratis: T('Kostenlos', 'Free') + (KATEGORIE_GRATIS[produkt.category] ? ' · ' + KATEGORIE_GRATIS[produkt.category] : ''),
    bald: T('Bald verfügbar', 'Coming soon'),
  }[art];
  const krumen = gratis
    ? [T('Kostenlos', 'Free'), `${p}${en ? '/free' : '/kostenlos'}`, art === 'gratis-panel' ? 'Extension' : (KATEGORIE_GRATIS[produkt.category] || S.katName)]
    : [T('Werkzeuge', 'Tools'), `${p}/shop`, S.katName];

  // ---------- Kaufbox ----------
  let kauf;
  if (art === 'werkzeug') kauf = { preis: euro(en, preis), klein: T('Einmalkauf · Installer inklusive', 'One-time purchase · installer included'), knopf: T('In den Warenkorb', 'Add to cart'), aktion: onWarenkorb };
  else if (art === 'abo') kauf = { preis: euro(en, ABO_MONAT), klein: T(`im Monat · oder ${euro(en, ABO_JAHR, 0)} im Jahr`, `a month · or ${euro(en, ABO_JAHR, 0)} a year`), knopf: T('7 Tage kostenlos testen', 'Try 7 days free'), aktion: () => setAbo('monat') };
  else if (art === 'gratis-panel') kauf = { preis: T('Kostenlos', 'Free'), klein: T('kommt mit dem kostenlosen Installer', 'comes with the free installer'), knopf: T('Installer laden', 'Get the installer'), aktion: e => zuAbschnitt(e, '#installer-sek') };
  else if (art === 'gratis') kauf = { preis: T('Kostenlos', 'Free'), klein: T('Direkter Download als .zip', 'Direct download as .zip'), knopf: T('Kostenlos laden', 'Download for free'), aktion: onDownload };
  else kauf = { preis: T('Bald', 'Soon'), klein: T('noch in Arbeit', 'still in the works'), knopf: null };

  // ---------- Preis-Entscheidung ----------
  const aioListe = [
    art === 'werkzeug' ? T(`${S.name} und alle anderen Werkzeuge`, `${S.name} and every other tool`) : T('Alle Werkzeuge für After Effects', 'Every tool for After Effects'),
    T('Studio Flow: alle in einem Panel, gibt es nur hier', 'Studio Flow: all of them in one panel, only here'),
    T('Instant Grade und Instant Comp inklusive', 'Instant Grade and Instant Comp included'),
    T('Jedes neue Werkzeug automatisch dabei', 'Every new tool included automatically'),
  ];
  const punktZeile = ([a, b, c]) => `${a}${a.endsWith('-') ? '' : ' '}${b}: ${c}`;
  const plan1 = art === 'abo'
    ? { titel: T('Monatlich', 'Monthly'), betrag: euro(en, ABO_MONAT), einheit: T('/ Monat', '/ month'), unter: T('monatlich kündbar', 'cancel any month'),
        liste: [T(`${S.name} im All-in-One`, `${S.name} in the All-in-One`), ...aioListe.slice(1)], knopf: T('7 Tage kostenlos testen', 'Try 7 days free'), aktion: () => setAbo('monat') }
    : gratis
      ? { titel: S.name, betrag: euro(en, 0, 0), einheit: T('kostenlos', 'free'), unter: '',
          liste: S.punkte.map(punktZeile), knopf: kauf.knopf, aktion: kauf.aktion }
      : { titel: T('Nur ', 'Just ') + S.name, betrag: euro(en, preis), einheit: T('einmalig', 'one-time'), unter: '',
          liste: [T(`${S.name} als eigenes Panel`, `${S.name} as its own panel`), ...S.punkte.slice(0, 2).map(punktZeile), T('Updates über den Installer', 'Updates through the installer')],
          knopf: T('In den Warenkorb', 'Add to cart'), aktion: onWarenkorb };
  const plan2 = { titel: art === 'abo' ? T('Jährlich', 'Yearly') : 'All-in-One', betrag: euro(en, ABO_JAHR, 0), einheit: T('/ Jahr', '/ year'),
    unter: T(`nur ${monatAusJahr} im Monat · 2 Monate geschenkt`, `only ${monatAusJahr} a month · 2 months free`),
    liste: art === 'abo' ? [T(`${S.name} im All-in-One`, `${S.name} in the All-in-One`), ...aioListe.slice(1)] : aioListe };

  // ---------- Fragen ----------
  const installieren = [T(`Wie installiere ich ${nameImSatz}?`, `How do I install ${nameImSatz}?`),
    T('Mit dem kostenlosen David Media Installer: laden, mit deinem Konto anmelden, dann in After Effects unter Fenster › Erweiterungen öffnen.',
      'With the free David Media Installer: download it, sign in with your account, then open it in After Effects under Window › Extensions.')];
  const konto = [T('Brauche ich ein Konto?', 'Do I need an account?'),
    T('Ja, ein kostenloses Konto. Darüber laufen der Installer und die Aktivierung.', 'Yes, a free account. The installer and activation run through it.')];
  let faq;
  if (art === 'werkzeug') {
    const eigene = S.punkte.find(x => x[0] === 'Eigene' || x[0] === 'Your own');
    faq = [installieren, konto,
      [T('Einzelkauf oder All-in-One?', 'Single purchase or All-in-One?'),
       T(`Einzeln gekauft bekommst du ${S.name} als eigenes Panel. Im All-in-One stecken alle Werkzeuge zusammen in einem Panel (Studio Flow), dazu Instant Grade, Instant Comp und alles Neue.`,
         `Bought on its own, you get ${S.name} as its own panel. The All-in-One puts every tool together in one panel (Studio Flow), plus Instant Grade, Instant Comp and everything new.`)]];
    if (eigene) faq.push([T(`Kann ich eigene ${eigene[1]} speichern?`, `Can I save my own ${eigene[1]}?`),
      T(`Ja. Eigene ${eigene[1]} lassen sich sichern und in jedem Projekt wiederverwenden.`, `Yes. You can save your own ${eigene[1]} and reuse them in every project.`)]);
  } else if (art === 'abo') {
    faq = [[T(`Kann ich ${S.name} einzeln kaufen?`, `Can I buy ${S.name} on its own?`),
            T(`Nein, ${S.name} gibt es nur im All-in-One. Darin stecken alle Werkzeuge, Instant Grade, Instant Comp und alles Neue.`, `No, ${S.name} is only available in the All-in-One, together with every tool, Instant Grade, Instant Comp and everything new.`)],
           [T('Was kostet das All-in-One?', 'What does the All-in-One cost?'),
            T(`${euro(en, ABO_MONAT)} im Monat oder ${euro(en, ABO_JAHR, 0)} im Jahr, das sind zwei Monate geschenkt. In den 7 Tagen Test wird nichts abgebucht, kündbar ist es jederzeit.`,
              `${euro(en, ABO_MONAT)} a month or ${euro(en, ABO_JAHR, 0)} a year, which gives you two months free. Nothing is charged during the 7-day trial, and you can cancel any time.`)],
           installieren, konto];
  } else if (art === 'gratis-panel') {
    faq = [[T(`Was kostet das ${S.name}?`, `What does the ${S.name} cost?`), T('Nichts. Es ist kostenlos und kommt mit dem Installer.', 'Nothing. It’s free and comes with the installer.')],
           installieren,
           [T('Fasst es meine Werkzeuge zu einem Panel zusammen?', 'Does it merge my tools into one panel?'),
            T('Nein. Es zeigt, was installiert ist, und öffnet jedes Werkzeug in seinem eigenen Panel. Alles in einem Panel gibt es mit Studio Flow im All-in-One.',
              'No. It shows what’s installed and opens each tool in its own panel. Everything in one panel comes with Studio Flow in the All-in-One.')]];
  } else if (art === 'gratis') {
    faq = [[T(`Was kostet ${S.name}?`, `What does ${S.name} cost?`), T('Nichts. Einfach laden und loslegen.', 'Nothing. Just download it and get going.')],
           [T('Wie benutze ich es?', 'How do I use it?'), S.schritte.map(([a, b]) => `${a}: ${b}`).join(' ')]];
    if (id === 'text-animations' || id === 'text-animations-2') faq.push([T('Brauche ich ein Plugin?', 'Do I need a plugin?'), T('Nein. Die Presets laufen in jedem After Effects.', 'No. The presets run in any After Effects.')]);
  }

  // ---------- Weitere ----------
  const andere = SICHTBAR.filter(x => x.id !== id && alleProdukte[x.id]);
  const verwandt = [...andere.filter(x => x.kat === S.kat), ...andere.filter(x => x.kat !== S.kat)].slice(0, 3).map(x => {
    const d = alleProdukte[x.id];
    return { id: x.id, name: d.name, bild: typeof d.images?.[0] === 'string' ? titelPfad(d.images[0], en) : null, preis: d.priceValue ? euro(en, d.priceValue) : T('Kostenlos', 'Free') };
  });


  // Hinweis unter dem Trenner und zweiter Knopf (wie „Buy with shop“ bei der Vorlage)
  const notiz = {
    werkzeug: T('Rabattcodes gibst du im Warenkorb ein.', 'Discount codes can be applied in the cart.'),
    abo: T('In den 7 Tagen wird nichts abgebucht. Jederzeit kündbar.', 'No charge during the 7-day trial. Cancel any time.'),
    'gratis-panel': T('Öffnet alle installierten David-Media-Werkzeuge mit einem Klick.', 'Opens every installed David Media tool in one click.'),
    gratis: T('Kostenlos für alle, kein Abo nötig.', 'Free for everyone, no subscription needed.'),
  }[art];

  return (
    <div className="produktseite" ref={wurzel}>
      <AboZustimmung offen={!!abo} plan={abo || 'jahr'} en={en} onZu={() => setAbo(null)} />
      <Lichtszene />
      {/* Kopf nach Vorlage (JerrySFX-Shop): links die Bild-Box mit den Ansichten und den Reitern darin,
          rechts linksbündig Name, Preis, kurzer Text und der Kaufknopf */}
      <section className="hero">
        <div className="hero-innen" ref={heroInnen}>
          <AnsichtBox key={id} ansichten={S.ansichten} />
          <div className="info">
            <div className="brotkrumen"><Link to={krumen[1]}>{krumen[0]}</Link>{krumen[2] ? <> / <span>{krumen[2]}</span></> : null}</div>
            <span className="kicker">{kicker}</span>
            <h1><span className="erstes">{erstes}</span> {rest.join(' ')}</h1>
            <div className="preiszeile"><span className="preis">{kauf.preis}</span><small>{kauf.klein}</small></div>
            {S.nutzen && <p className="einzeiler">{ganz(S.nutzen)}</p>}
            <div className="unten">
            {art !== 'bald' && <div className="trenner" />}
            {notiz && <p className="notiz">{notiz}</p>}
            {kauf.knopf && (
              <div className="knoepfe">
                <a className="btn primaer" href="#preise" onClick={e => { e.preventDefault(); kauf.aktion(e); }} aria-haspopup={art === 'abo' ? 'dialog' : undefined} data-testid={{ werkzeug: 'add-to-cart-btn', abo: 'abo-btn', gratis: 'download-btn', 'gratis-panel': 'installer-btn' }[art]}>{kauf.knopf}</a>
                {art !== 'abo' && (
                  <Link className="btn glas" to={`${p}/shop/all-in-one`}>{T('Alle im All-in-One', 'All in the All-in-One')}<small>{T(`ab ${monatAusJahr} im Monat`, `from ${monatAusJahr} a month`)}</small></Link>
                )}
              </div>
            )}
            {geladen && art === 'gratis' && <div className="geladen">{T('Download gestartet.', 'Download started.')}</div>}
            </div>
          </div>
        </div>
      </section>

      {art !== 'bald' && (
        <>
          <section className="sek" id="live" style={{ paddingTop: 50 }}>
            <div className="innen">
              <div className="kopf reveal-text">
                <span className="kicker">{T('In Aktion', 'In action')}</span>
                <h2><span className="zl"><span>{T('So sieht es aus.', 'This is how it looks.')}</span></span><span className="zl"><span className="grau">{T('Direkt aus After Effects.', 'Straight from After Effects.')}</span></span></h2>
                <div className="versprechen reveal"><s>{S.versprechen[0]}</s><b>{S.versprechen[1]}</b></div>
              </div>
              <div className={'demo reveal' + (hatVideo ? ' hat-video' : '')}>
                {/* Ohne Video (noch keins da oder es lädt nicht): eine scharfe Ansicht aus dem Panel statt
                    eines Platzhalters. Lieber die Einstellungen als die Vorlagen, die zeigt die Box oben schon. */}
                <div className="standbild"><Bild a={standbild} /></div>
                {S.video && (
                  <DemoPlayer src={S.video} en={en} onBereit={() => setHatVideo(true)} onFehler={() => setHatVideo(false)} />
                )}
              </div>
              {/* So funktioniert's als AE-Zeitleiste: Linie mit drei Keyframe-Rauten, darunter die Schritte */}
              <div className="zeitstrahl reveal">
                <div className="linie"><b /></div>
                {S.schritte.map(([titel, text], i) => (
                  <div className="punkt" key={titel} style={{ '--i': i }}>
                    <i className="raute" />
                    <span className="tc">00:00:0{i + 1}:00</span>
                    <h3>{titel}</h3>
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {mitInstaller && (
            <section className="sek" id="installer-sek">
              <div className="innen inst">
                <div className="inst-bild reveal"><img src="/produkt/installer.webp" alt="" loading="lazy" /></div>
                <div className="reveal-text">
                  <span className="kicker">{T('Installation', 'Installation')}</span>
                  <h2><span className="zl"><span>{T('In einer Minute drin.', 'Up and running in a minute.')}</span></span><span className="zl"><span className="grau">{T('Und immer aktuell.', 'And always up to date.')}</span></span></h2>
                  <p className="lead reveal">{T(`Der kostenlose David Media Installer richtet ${nameImSatz} ein und hält es aktuell. Alles, was du besitzt, liegt an einem Ort.`,
                    `The free David Media Installer sets up ${nameImSatz} and keeps it up to date. Everything you own, in one place.`)}</p>
                  <ol className="schritte reveal">
                    <li><span><b>{T('Installer laden', 'Download the installer')}</b>{T(' für Mac oder Windows.', ' for Mac or Windows.')}</span></li>
                    <li><span><b>{T('Mit deinem Konto anmelden.', 'Sign in with your account.')}</b>{T(' Das Konto ist kostenlos.', ' The account is free.')}</span></li>
                    <li><span><b>{T('In After Effects öffnen:', 'Open it in After Effects:')}</b>{T(' Fenster › Erweiterungen.', ' Window › Extensions.')}</span></li>
                  </ol>
                  <div className="ctas reveal">
                    <a className="btn primaer" href={info ? info.url : undefined}>{T('Für Mac laden', 'Download for Mac')}</a>
                    <a className="btn glas" href={info ? info.url_win : undefined}>{T('Für Windows laden', 'Download for Windows')}</a>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="sek" id="preise">
            <div className="innen">
              <div className="kopf reveal-text">
                <span className="kicker">{T('Preis', 'Price')}</span>
                <h2>
                  <span className="zl"><span>{art === 'abo' ? T('Monatlich oder jährlich.', 'Monthly or yearly.') : gratis ? T('Kostenlos oder alles.', 'Free or everything.') : T('Einzeln oder alles.', 'One tool or everything.')}</span></span>
                  <span className="zl"><span className="grau">{art === 'abo' ? T('Jederzeit kündbar.', 'Cancel any time.') : T('Du entscheidest.', 'Your call.')}</span></span>
                </h2>
              </div>
              <div className="wahl">
                <div className="plan reveal fx">
                  <h3>{plan1.titel}</h3>
                  <div className="betrag">{plan1.betrag} <small>{plan1.einheit}</small></div>
                  <div className="unter">{plan1.unter}</div>
                  <ul>{plan1.liste.map(x => <li key={x}>{x}</li>)}</ul>
                  {plan1.knopf && <a className="btn glas" href="#preise" onClick={e => { e.preventDefault(); plan1.aktion(e); }} aria-haspopup={art === 'abo' ? 'dialog' : undefined} data-testid="plan1-btn">{plan1.knopf}</a>}
                  <Fx />
                </div>
                <div className="plan top reveal fx" style={{ transitionDelay: '.1s' }}>
                  <span className="marke">{T('Empfohlen', 'Recommended')}</span>
                  <h3>{plan2.titel}</h3>
                  <div className="betrag">{plan2.betrag} <small>{plan2.einheit}</small></div>
                  <div className="unter">{plan2.unter}</div>
                  <ul>{plan2.liste.map(x => <li key={x}>{x}</li>)}</ul>
                  <div className="mikro" style={{ marginBottom: 12 }}>{T('In den 7 Tagen wird nichts abgebucht. Jederzeit kündbar.', 'No charge during the trial. Cancel any time.')}</div>
                  <button type="button" className="btn primaer" onClick={() => setAbo('jahr')} aria-haspopup="dialog" data-testid="plan-jahr-btn">{T('7 Tage kostenlos testen', 'Try 7 days free')}</button>
                  <Fx />
                </div>
              </div>
            </div>
          </section>

          {faq && (
            <section className="sek" style={{ paddingTop: 20 }}>
              <div className="innen">
                <span className="kicker">{T('Fragen', 'Questions')}</span>
                <h2>{T('Gut zu wissen.', 'Good to know.')}</h2>
                <div className="faq">
                  {faq.map(([f, a], i) => <details key={f} open={i === 0}><summary>{f}</summary><p>{a}</p></details>)}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <section className="sek" style={{ paddingTop: 20 }}>
        <div className="innen">
          <span className="kicker">{T('Passt dazu', 'Goes well with')}</span>
          <h2>{gratis ? T('Mehr zum Entdecken.', 'More to explore.') : T('Weitere Werkzeuge.', 'More tools.')}</h2>
          <div className="verwandt">
            {verwandt.map(v => (
              <Link key={v.id} className="vkarte fx" to={`${p}/shop/${v.id}`}>
                {v.bild && <img {...titelBild(v.bild, false, '(max-width: 960px) 100vw, 33vw')} alt="" loading="lazy" />}
                <div><b>{v.name}</b><span>{v.preis}</span></div>
                <Fx />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
